import { useLatestCallback } from 'hook/useLatestCallback'
import { useRefState } from 'hook/useRefState'
import { RefCallback, RefObject, useEffect } from 'react'

const isTouchDevice = () => {
  try {
    document.createEvent('TouchEvent')
    return true
  } catch {
    return false
  }
}

type E = HTMLElement

/** `ignore` marks another element (e.g. the button that toggles the popup) that does not count as outside */
export function useClickOutside<T extends E = E>(
  callback: () => void,
  ignore?: RefObject<HTMLElement | null>,
): RefCallback<T | null | undefined> {
  const [ref, onRef] = useRefState<T>()

  const listener = useLatestCallback((event: MouseEvent | TouchEvent) => {
    const target = event.target as Node
    if (ref && !ref.contains(target) && !ignore?.current?.contains(target)) callback()
  })

  useEffect(() => {
    const type = isTouchDevice() ? 'touchend' : 'mouseup'
    document.addEventListener(type, listener)
    return () => document.removeEventListener(type, listener)
  }, [listener, ref])

  return onRef
}
